import React, { useRef, useEffect, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';
import TeamMessageItem from './TeamMessageItem';

export default function TeamMessageList({
    messages,
    hasMore,
    loadingMore,
    onLoadMore,
    onReply,
    onEdit,
    onDelete,
    onToggleReaction,
    isConversationAdmin,
    onAiAction,
    onSaveToNotes,
    onCreateTask,
    onViewNote
}) {
    const { theme } = useTheme();
    const listRef = useRef(null);
    const bottomRef = useRef(null);
    const prevMessageCountRef = useRef(0);
    const isAtBottomRef = useRef(true);

    // Track scroll position
    const handleScroll = useCallback(() => {
        if (!listRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = listRef.current;
        isAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 50;

        // Load more when scrolled to top
        if (scrollTop < 100 && hasMore && !loadingMore) {
            onLoadMore();
        }
    }, [hasMore, loadingMore, onLoadMore]);

    // Auto-scroll to bottom for new messages
    useEffect(() => {
        if (messages.length > prevMessageCountRef.current && isAtBottomRef.current) {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
        prevMessageCountRef.current = messages.length;
    }, [messages.length]);

    // Scroll to bottom on initial load
    useEffect(() => {
        if (messages.length > 0) {
            bottomRef.current?.scrollIntoView();
        }
    }, [messages.length > 0]);

    const scrollToMessage = (messageId) => {
        const el = document.getElementById(`msg-${messageId}`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('bg-orange-100/20');
            setTimeout(() => el.classList.remove('bg-orange-100/20'), 2000);
        }
    };

    // Group messages by date
    const groupedMessages = [];
    let currentDate = '';

    messages.forEach((msg) => {
        const date = new Date(msg.created_at).toLocaleDateString([], {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
        });
        if (date !== currentDate) {
            currentDate = date;
            groupedMessages.push({ type: 'date', date });
        }
        groupedMessages.push({ type: 'message', data: msg });
    });

    return (
        <div
            ref={listRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto overflow-x-hidden min-w-0"
        >
            {loadingMore && (
                <div className="flex justify-center py-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500" />
                </div>
            )}

            {!hasMore && messages.length > 0 && (
                <div className={`text-center py-4 text-xs font-rasa ${
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                }`}>
                    Beginning of conversation
                </div>
            )}

            {messages.length === 0 && !loadingMore && (
                <div className="flex flex-col items-center justify-center h-full">
                    <p className={`text-sm font-rasa ${
                        theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                    }`}>No messages yet. Start the conversation!</p>
                </div>
            )}

            {groupedMessages.map((item, index) => {
                if (item.type === 'date') {
                    return (
                        <div key={`date-${index}`} className="flex items-center gap-3 px-4 py-2 my-2">
                            <div className={`flex-1 h-px ${
                                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                            }`} />
                            <span className={`text-xs font-rasa px-2 ${
                                theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                            }`}>{item.date}</span>
                            <div className={`flex-1 h-px ${
                                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                            }`} />
                        </div>
                    );
                }

                return (
                    <TeamMessageItem
                        key={item.data.id}
                        message={item.data}
                        onReply={onReply}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onToggleReaction={onToggleReaction}
                        onScrollToMessage={scrollToMessage}
                        isConversationAdmin={isConversationAdmin}
                        onAiAction={onAiAction}
                        onSaveToNotes={onSaveToNotes}
                        onCreateTask={onCreateTask}
                        onViewNote={onViewNote}
                    />
                );
            })}

            <div ref={bottomRef} />
        </div>
    );
}
